import { DataGridPro } from '@mui/x-data-grid-pro';
import { useIsPhone } from '../hooks/useIsMobile';
import { useResponsiveColumns } from '../hooks/useResponsiveColumns';
import MobileCardList from './MobileCardList';

/**
 * Auto-switches between DataGridPro (desktop) and MobileCardList (phone).
 *
 * Usage:
 *   <ResponsiveDataView
 *     rows={data}
 *     columns={columns}
 *     cardRenderer={(row) => <MyCard row={row} />}
 *     onRowClick={(row) => navigate(`/detail/${row.id}`)}
 *     getRowId={(row) => row.MyID}
 *     // All other DataGridPro props pass through on desktop
 *     paginationMode="server"
 *     paginationModel={paginationModel}
 *     ...
 *   />
 *
 * Pass `GridComponent` prop to use DataGrid instead of DataGridPro:
 *   <ResponsiveDataView GridComponent={DataGrid} ... />
 *
 * On phones (<600px) with a cardRenderer: renders MobileCardList.
 * On tablets/desktop or without cardRenderer: renders the grid with responsive columns.
 */
const ResponsiveDataView = ({
  rows,
  columns,
  cardRenderer,
  onRowClick,
  getRowId,
  loading,
  emptyMessage,
  GridComponent,
  // Mobile pagination (for MobileCardList)
  mobilePage,
  mobilePageSize = 25,
  mobileTotalRows,
  onMobilePageChange,
  // Everything else goes to DataGridPro
  ...dataGridProps
}) => {
  const isPhone = useIsPhone();
  const responsiveColumns = useResponsiveColumns(columns);
  const Grid = GridComponent || DataGridPro;

  // Phone with card renderer → mobile card list
  if (isPhone && cardRenderer) {
    return (
      <MobileCardList
        rows={rows}
        cardRenderer={cardRenderer}
        onRowClick={onRowClick}
        loading={loading || dataGridProps.loading}
        getRowId={getRowId || dataGridProps.getRowId}
        emptyMessage={emptyMessage}
        page={mobilePage}
        pageSize={mobilePageSize}
        totalRows={mobileTotalRows}
        onPageChange={onMobilePageChange}
      />
    );
  }

  // Desktop/tablet → Grid with responsive columns
  return (
    <Grid
      rows={rows}
      columns={responsiveColumns}
      getRowId={getRowId}
      loading={loading}
      {...dataGridProps}
    />
  );
};

export default ResponsiveDataView;
